// src/components/programs/ProgramForm/Step2Sections/ProgramTaxSection.jsx

import React, { useEffect } from "react";
import { Grid, Box, Alert, Typography, Stack, alpha, useTheme } from "@mui/material";
import {
    InfoOutlined as InfoIcon,
    ReceiptLongOutlined as TaxIcon,
} from "@mui/icons-material";
import FormInput from "../../../inputs/FormInput";

const ProgramTaxSection = ({
    formData,
    totals,
    onTaxChange,
    errors,
    isArabic,
}) => {
    const theme = useTheme();

    const taxTypeOptions = [
        { id: "none", name: isArabic ? "بدون ضريبة" : "No Tax" },
        { id: "amount", name: isArabic ? "مبلغ ثابت" : "Fixed Amount" },
        { id: "percentage", name: isArabic ? "نسبة مئوية" : "Percentage" },
    ];

    // Handle clearing the values if the type is "none"
    useEffect(() => {
        if (formData?.tax_input_type === "none" || !formData?.tax_input_type) {
            if (formData?.tax_amount !== 0 || formData?.tax_percentage !== 0) {
                onTaxChange("tax_amount", 0);
                onTaxChange("tax_percentage", 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.tax_input_type]);

    const handlePercentageChange = (e) => {
        const val = parseFloat(e.target.value) || 0;
        onTaxChange("tax_percentage", val);
    };

    const handleAmountChange = (e) => {
        const val = parseFloat(e.target.value) || 0;
        onTaxChange("tax_amount", val);
    };

    const isPercentage = formData?.tax_input_type === "percentage";
    const isAmount = formData?.tax_input_type === "amount";

    return (
        <Box>
            {/* Compact Header with Info */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <TaxIcon sx={{ fontSize: 18, color: 'error.main' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                        {isArabic ? "ضريبة البرنامج" : "Program Tax"}
                    </Typography>
                </Box>
            </Stack>

            {/* Compact Info Alert */}
            <Alert
                severity="info"
                icon={<InfoIcon sx={{ fontSize: 18 }} />}
                sx={{
                    mb: 2,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    '& .MuiAlert-icon': {
                        fontSize: 18,
                        mr: 1,
                        color: 'error.main'
                    },
                    '& .MuiAlert-message': {
                        padding: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis'
                    },
                }}
            >
                <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isArabic
                        ? "تُطبق الضريبة على الإجمالي النهائي للبرنامج (بعد الخصم) ولا تؤثر على الأرباح المسجلة."
                        : "Tax is applied to the final program total (after discount) and does not affect recorded profits."}
                </Typography>
            </Alert>

            <Grid container spacing={1.5}>
                {/* Tax Type */}
                <Grid item xs={12}>
                    <FormInput
                        label={isArabic ? "نوع الضريبة" : "Tax Type"}
                        type="select"
                        name="tax_input_type"
                        value={formData?.tax_input_type || "none"}
                        onChange={(e) => onTaxChange("tax_input_type", e.target.value || "none")}
                        options={taxTypeOptions}
                        required
                        size="small"
                    />
                </Grid>

                {/* Tax Percentage */}
                <Grid item xs={12} sm={6}>
                    <FormInput
                        label={isArabic ? "نسبة الضريبة (%)" : "Tax Percentage (%)"}
                        type="number"
                        name="tax_percentage"
                        placeholder={isArabic ? "أدخل النسبة" : "Enter percentage"}
                        value={isPercentage ? (formData?.tax_percentage || "") : (totals?.tax_percentage ? totals.tax_percentage.toFixed(2) : "")}
                        onChange={handlePercentageChange}
                        disabled={!isPercentage}
                        error={!!errors.tax_percentage}
                        helperText={errors.tax_percentage}
                        inputProps={{
                            min: 0,
                            max: 100,
                            step: 1,
                        }}
                        size="small"
                    />
                </Grid>

                {/* Tax Amount */}
                <Grid item xs={12} sm={6}>
                    <FormInput
                        label={isArabic ? "قيمة الضريبة" : "Tax Amount"}
                        type="number"
                        name="tax_amount"
                        placeholder={isArabic ? "أدخل القيمة" : "Enter amount"}
                        value={isAmount ? (formData?.tax_amount || "") : (totals?.tax_amount ? totals.tax_amount.toFixed(2) : "")}
                        onChange={handleAmountChange}
                        disabled={!isAmount}
                        error={!!errors.tax_amount}
                        helperText={errors.tax_amount}
                        inputProps={{
                            min: 0,
                            step: 0.01,
                        }}
                        size="small"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProgramTaxSection;
